import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import CategoryList from "@/polymet/components/category-list";
import FeaturedProducts from "@/polymet/components/featured-products";
import { CategoryService } from "@/lib/category-service";
import { ProductService } from "@/lib/product-service";
import { Category as CategoryType, Subcategory, ProductCategory as ProductCategoryType } from "@/lib/category-service";
import { Loader2, Globe, ShieldCheck, Lock, Headphones, Quote, Clock, TrendingUp, Eye, ArrowRight, BarChart3 } from "lucide-react";
import HeroSection from "@/polymet/components/hero-section";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import BreadcrumbNavigation from '@/polymet/components/breadcrumb-navigation';
import { supabase } from '@/lib/supabase';
import { useAnalytics } from '@/polymet/hooks/use-analytics';
import { analyticsService, RecentlyVisitedCategory } from '@/polymet/services/analytics-service';
import { useAuth } from '@/lib/auth-context';

export default function HomePage() {
  // State for categories, subcategories, and product categories
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [productCategories, setProductCategories] = useState<ProductCategoryType[]>([]);
  const [featuredProducts, setFeaturedProducts] = useState<any[]>([]);
  const [recentRFQs, setRecentRFQs] = useState<any[]>([]);
  
  // Analytics state
  const [recentlyVisited, setRecentlyVisited] = useState<RecentlyVisitedCategory[]>([]);
  const [trendingCategories, setTrendingCategories] = useState<any[]>([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  
  // Progressive loading states
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(true);
  const [productsLoading, setProductsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Auth and analytics hooks
  const { user } = useAuth();
  const { trackCategoryVisit, getUserRecentlyVisited } = useAnalytics();

  // Load analytics data
  useEffect(() => {
    const loadAnalyticsData = async () => {
      try {
        setAnalyticsLoading(true);

        // Load user's recently visited categories (only if logged in)
        if (user) {
          const userRecentCategories = await getUserRecentlyVisited(6);
          setRecentlyVisited(userRecentCategories);
        }

        // Load trending categories (public data)
        const popularCategories = await analyticsService.getPopularCategories(7, 8);
        const trendingSearches = await analyticsService.getSearchTrends(7, 5);

        // Transform trending data for display
        const trending = popularCategories.map((category, index) => ({
          id: category.category_id || category.subcategory_id || category.product_category_id,
          name: getCategoryDisplayName(category),
          visits: category.visit_count,
          growth: index < popularCategories.length / 2 ? '+' + (15 + Math.floor(Math.random() * 20)) + '%' : '+' + (5 + Math.floor(Math.random() * 15)) + '%',
          type: category.visit_type,
          isHot: index < 3
        }));

        setTrendingCategories(trending);
      } catch (error) {
        console.error('Error loading analytics data:', error);
      } finally {
        setAnalyticsLoading(false);
      }
    };

    loadAnalyticsData();
  }, [user]);

  // Helper function to get display name for categories
  const getCategoryDisplayName = (category: any) => {
    const id = category.category_id || category.subcategory_id || category.product_category_id;
    
    // Map common category IDs to friendly names
    const categoryNames: Record<string, string> = {
      'construction-equipment': 'Construction Equipment',
      'excavators': 'Excavators',
      'manufacturing-tools': 'Manufacturing Tools',
      'agricultural-machinery': 'Agricultural Machinery',
      'earthmoving': 'Earthmoving Equipment',
      'bulldozers': 'Bulldozers',
      'cranes': 'Cranes',
      'generators': 'Generators',
      'compressors': 'Compressors',
      'welding-equipment': 'Welding Equipment'
    };

    return categoryNames[id] || id?.replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Unknown Category';
  };

  // Handle category click with analytics tracking
  const handleCategoryClick = async (categoryId: string, categoryType: 'category' | 'subcategory' | 'product_category' = 'category', source: string = 'navigation') => {
    try {
      await trackCategoryVisit({
        categoryId: categoryType === 'category' ? categoryId : undefined,
        subcategoryId: categoryType === 'subcategory' ? categoryId : undefined,
        productCategoryId: categoryType === 'product_category' ? categoryId : undefined,
        visitType: categoryType,
        source: source as any
      });
    } catch (error) {
      console.error('Error tracking category visit:', error);
    }
  };

  // Load data progressively - non-blocking
  useEffect(() => {
    // Load essential data first (categories)
    const loadEssentialData = async () => {
      try {
        const categoryService = new CategoryService();
        
        // Load only categories first for immediate display
        const categoriesData = await categoryService.getCategories();
        setCategories(categoriesData);
        setCategoriesLoading(false);
        
        // Load subcategories and product categories in background
        const subcategoriesPromises = categoriesData.map((category: CategoryType) => 
          categoryService.getSubcategoriesByCategoryId(category.id)
        );
        const subcategoriesResults = await Promise.all(subcategoriesPromises);
        const allSubcategories = subcategoriesResults.flat();
        setSubcategories(allSubcategories);

        // Load product categories
        const productCategoriesPromises = allSubcategories.map((subcategory: Subcategory) =>
          categoryService.getProductCategoriesBySubcategoryId(subcategory.id)
        );
        const productCategoriesResults = await Promise.all(productCategoriesPromises);
        const allProductCategories = productCategoriesResults.flat();
        setProductCategories(allProductCategories);
        
      } catch (err) {
        console.error('Error loading categories:', err);
        setError('Failed to load categories');
        setCategoriesLoading(false);
      }
    };

    // Load secondary data (products, RFQs)
    const loadSecondaryData = async () => {
      try {
        const productService = new ProductService();
        
        // Load featured products
        const featuredProductsData = await productService.getFeaturedProducts();
        setFeaturedProducts(featuredProductsData);
        
        // Load recent RFQs
        const { data: rfqsData, error: rfqsError } = await supabase
          .from('rfq_requests')
          .select(`
            *,
            user:accounts!rfq_requests_user_id_fkey(full_name, company_name),
            category:categories!rfq_requests_category_id_fkey(name)
          `)
          .eq('status', 'published')
          .gte('expiry_date', new Date().toISOString())
          .order('created_at', { ascending: false })
          .limit(6);

        if (rfqsError) throw rfqsError;
        setRecentRFQs(rfqsData || []);
        
        setProductsLoading(false);
      } catch (err) {
        console.error('Error loading secondary data:', err);
        setProductsLoading(false);
      }
    };

    // Start both loading processes
    loadEssentialData();
    loadSecondaryData();
  }, []);

  return (
    <div className="space-y-8">
      {/* Hero Section - Shows immediately */}
      <HeroSection
        title="Find the Perfect Machinery for Your Business"
        subtitle="India's largest marketplace for industrial machinery and equipment"
        backgroundImage="/images/hero_image.png"
        onSearch={(query) => console.log('Search:', query)}
        onCategoryClick={(category) => console.log('Category clicked:', category)}
      />

      {/* Recently Visited Categories - Only show for logged-in users */}
      {user && recentlyVisited.length > 0 && (
        <section className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Recently Visited Categories</h2>
                  <p className="text-sm text-blue-600">Quick access to your frequently browsed categories</p>
                </div>
              </div>
              <div className="flex items-center gap-1 text-xs text-blue-600">
                <BarChart3 className="h-3 w-3" />
                <span>Powered by your activity</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
              {recentlyVisited.map((visit, index) => {
                const categoryId = visit.category_id || visit.subcategory_id || visit.product_category_id || '';
                const displayName = getCategoryDisplayName(visit);
                
                return (
                  <Link
                    key={visit.id}
                    to={`/categories/${categoryId}`}
                    onClick={() => handleCategoryClick(categoryId, visit.visit_type, 'recently_visited')}
                    className="group"
                  >
                    <div className="bg-white rounded-lg p-4 border border-blue-100 hover:border-blue-300 hover:shadow-md transition-all duration-200 group-hover:scale-105">
                      <div className="text-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2 group-hover:bg-blue-200 transition-colors">
                          <Eye className="h-5 w-5 text-blue-600" />
                        </div>
                        <h3 className="font-medium text-sm text-gray-900 line-clamp-2 group-hover:text-blue-800">
                          {displayName}
                        </h3>
                        <div className="flex items-center justify-center gap-1 mt-1">
                          <span className="text-xs text-blue-600">{visit.visit_count} visits</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            
            <div className="mt-4 text-center">
              <Link to="/buyer-dashboard-enhanced" className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium">
                View all activity
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Categories Section - Progressive loading */}
      <section className="container mx-auto px-4">
        {categoriesLoading ? (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <div className="h-8 bg-gray-200 rounded-md w-48 mx-auto animate-pulse"></div>
              <div className="h-4 bg-gray-200 rounded-md w-64 mx-auto animate-pulse"></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-gray-200 h-32 rounded-lg animate-pulse"></div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-lg text-red-500 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>Try Again</Button>
          </div>
        ) : (
          <CategoryList 
            categories={categories} 
            subcategories={subcategories} 
            productCategories={productCategories} 
            title="Browse by Category"
            description="Explore our wide range of machinery categories for your business needs in India"
          />
        )}
      </section>

      {/* Featured Products Section - Progressive loading */}
      <section className="container mx-auto px-4">
        <FeaturedProducts
          products={featuredProducts}
          title="Featured Machinery"
          description="Discover our handpicked selection of quality machinery"
          loading={productsLoading}
          viewAllLink="/products"
        />
      </section>

      {/* Trending Categories - Public section */}
      <section className="container mx-auto px-4">
        <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-6 border border-orange-100">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Trending Categories</h2>
                <p className="text-sm text-orange-600">See what other buyers are exploring right now</p>
              </div>
            </div>
            <div className="flex items-center gap-1 text-xs text-orange-600">
              <BarChart3 className="h-3 w-3" />
              <span>Live marketplace data</span>
            </div>
          </div>
          
          {analyticsLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg p-4 border border-orange-100 animate-pulse">
                  <div className="w-10 h-10 bg-gray-200 rounded-full mx-auto mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded mb-1"></div>
                  <div className="h-2 bg-gray-200 rounded w-16 mx-auto"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-3">
              {trendingCategories.slice(0, 8).map((category, index) => (
                <Link
                  key={category.id}
                  to={`/categories/${category.id}`}
                  onClick={() => handleCategoryClick(category.id, category.type, 'trending')}
                  className="group"
                >
                  <div className="bg-white rounded-lg p-4 border border-orange-100 hover:border-orange-300 hover:shadow-md transition-all duration-200 group-hover:scale-105 relative">
                    {category.isHot && (
                      <Badge className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-1.5 py-0.5">
                        🔥 Hot
                      </Badge>
                    )}
                    <div className="text-center">
                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2 group-hover:bg-orange-200 transition-colors">
                        <TrendingUp className="h-5 w-5 text-orange-600" />
                      </div>
                      <h3 className="font-medium text-sm text-gray-900 line-clamp-2 group-hover:text-orange-800">
                        {category.name}
                      </h3>
                      <div className="flex items-center justify-center gap-2 mt-1">
                        <span className="text-xs text-orange-600">{category.visits} visits</span>
                        <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                          {category.growth}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
          
          <div className="mt-4 text-center">
            <Link to="/analytics-demo" className="inline-flex items-center gap-2 text-sm text-orange-600 hover:text-orange-800 font-medium">
              View live analytics
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* Popular Brands Section */}
      <section className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-4">Popular Brands</h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Explore top machinery manufacturers available in India
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {[
            {
              name: "Caterpillar",
              shortName: "CAT",
            },
            {
              name: "JCB",
              shortName: "JCB",
            },
            {
              name: "Komatsu",
              shortName: "KOMATSU",
            },
            {
              name: "Tata Hitachi",
              shortName: "TATA HITACHI",
            },
            {
              name: "Mahindra",
              shortName: "MAHINDRA",
            },
            {
              name: "BEML",
              shortName: "BEML",
            },
          ].map((brand, index) => (
            <Link
              key={index}
              to={`/brand/${brand.name.toLowerCase().replace(' ', '-')}`}
              className="group"
            >
              <div className="bg-white rounded-lg overflow-hidden shadow-md transition-all duration-300 group-hover:shadow-lg group-hover:-translate-y-1 border border-gray-200 group-hover:border-orange-300">
                <div className="h-32 relative flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 group-hover:from-orange-50 group-hover:to-orange-100 transition-all duration-300">
                  <h3 className="text-gray-700 group-hover:text-orange-700 font-bold text-sm text-center px-3 transition-colors duration-300">
                    {brand.shortName}
                  </h3>
                </div>
                <div className="bg-white py-3 px-3 text-center">
                  <h3 className="text-xs font-medium text-gray-600 group-hover:text-gray-800 transition-colors duration-300">{brand.name}</h3>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">
            Why Choose MachineSearch.in
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            India's trusted marketplace for buying and selling machinery
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            {
              title: "Pan-India Network",
              description:
                "Connect with sellers and buyers from across the country",
              icon: Globe,
            },
            {
              title: "Verified Indian Sellers",
              description:
                "All sellers undergo thorough verification with proper GST and business details",
              icon: ShieldCheck,
            },
            {
              title: "Secure Transactions",
              description:
                "Multiple secure payment options including UPI, net banking, and EMI",
              icon: Lock,
            },
            {
              title: "Local Support",
              description:
                "Our expert team is available in multiple Indian languages",
              icon: Headphones,
            },
          ].map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <div key={index} className="bg-card p-6 rounded-lg text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center">
                  <IconComponent className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-orange-50 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">What Our Customers Say</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Hear from businesses across India who found success with
              MachineSearch.in
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote:
                  "Found exactly the excavator I needed for our construction project in Pune. The seller was verified and the transaction was smooth.",
                author: "Rajesh Sharma",
                company: "Sharma Construction, Mumbai",
                avatar: "https://github.com/yusufhilmi.png",
              },
              {
                quote:
                  "As a machinery dealer in Chennai, this platform has helped me reach customers from all over India. My sales have increased by 40%.",
                author: "Priya Patel",
                company: "Southern Machinery, Chennai",
                avatar: "https://github.com/furkanksl.png",
              },
              {
                quote:
                  "Excellent quality machinery at competitive prices. The sales team provided thorough consultation to help us find the right equipment for our manufacturing unit.",
                author: "Amit Singh",
                company: "Singh Industries, Delhi",
                avatar: "https://github.com/kdrnp.png",
              },
            ].map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md relative">
                <Quote className="absolute top-4 right-4 w-6 h-6 text-orange-200" />
                <p className="italic mb-4 relative">"{testimonial.quote}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.author}
                    className="w-12 h-12 rounded-full mr-4"
                    loading="lazy"
                    decoding="async"
                  />

                  <div>
                    <h4 className="font-semibold">{testimonial.author}</h4>
                    <p className="text-sm text-muted-foreground">
                      {testimonial.company}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-orange-600 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Buy or Sell Machinery?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join thousands of businesses across India who trust MachineSearch.in
            for their equipment needs
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button size="lg" variant="secondary" asChild>
              <Link to="/register">Create an Account</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent border-white text-white hover:bg-white hover:text-orange-600"
              asChild
            >
              <Link to="/contact">Contact Sales</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
